import { useForm } from 'react-hook-form';
import { ConfirmationDialog, DrawerComp, FundingDrawersDropdown } from '../../components/molecules';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Typography, BasicButton } from '../../components/atoms';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddIncomeSourcesForm } from './add-income-sources-form';
import { useAppSelector, useAppDispatch } from '../../common/hooks';
import { clearClientError, saveClient, updateClientIncomeSources } from '../../features/client';
import {
    CLIENT_PROFILE_STAGE4,
    DEFAULT_PLANNING_AGE,
    DEFAULT_RETIREMENT_AGE,
    EWS_DURATION_KEYS,
    FEATURE_NAMES,
    STATUSVARIANTS
} from '../../common/constants';
import { v4 as uuidv4 } from 'uuid';
import { isEqual, checkFFEnabled, removeNonNumeric, getClientJourneyFlowType } from '../../utils';
import incomeSource from '../../assets/icons/income_source.svg';
import { addAgeToDate, getYYYYMMDDfromDate, removeNullUndefined } from '../../common/helper';
import { CallRunpipePropsType, IncomeSourcesType } from '../../common/types';
import dayjs, { Dayjs } from 'dayjs';
import { syncAdviceGoals } from '../../features/client-goals';
import { setAffectedEwsTerms } from '../../features/ews';
import { updateGlobalLoaderState } from '../../features/global/globalSlice';

interface AddIncomeSourcesDrawerProps {
    openDrawer: boolean;
    setOpenDrawer: any;
    mode?: 'add' | 'edit';
    /* eslint-disable no-unused-vars */
    selectedAccountId?: string;
    onEditSuccess?: (deletedAccounts?: string[]) => void;
    callRunpipe?: CallRunpipePropsType;
    triggerDeleteAccountId?: string;
    onDeleteTriggered?: () => void;
    needPrepopulationOnEdit?: any;
}

interface AddIncomeSourcesFormInput {
    accountId: string;
    sourceName: string;
    sourceType: string;
    sourceOwner: string;
    amount: string | number;
    frequency: string;
    beginningYear: Dayjs | null;
    beginningYearKey: string;
    endYear: Dayjs | null;
    endYearKey: string;
    escalateIncome: number;
    update?: boolean;
    isNew?: boolean;
}
const WIDTH = '396px';

export const DeleteContainer = styled('div')(({ theme }) => ({
    textAlign: 'center',
    marginTop: '24px',
    marginBottom: '30px',
    borderTop: `1px solid ${theme.palette.semantic.bd.default}`,
    paddingTop: '8px'
}));

const AddIncomeSourcesDrawer = (props: AddIncomeSourcesDrawerProps) => {
    const clientData = useAppSelector((state) => state.adviceClient);
    const global = useAppSelector((state) => state.global);
    const {
        openDrawer,
        setOpenDrawer,
        mode = 'add',
        onEditSuccess,
        callRunpipe,
        selectedAccountId = '',
        triggerDeleteAccountId,
        onDeleteTriggered,
        needPrepopulationOnEdit
    } = props;
    const [addedSources, setAddedSources] = useState<(IncomeSourcesType | AddIncomeSourcesFormInput)[]>(
        clientData?.accounts?.incomeSources || []
    );
    const [deletedAccountIds, setDeletedAccountIds] = useState<string[]>([]);
    const [keyId, setKeyId] = useState(uuidv4);
    const [needPrepopulation, setNeedPrepopulation] = useState(true);
    const [selectedAccountForEdit, setSelectedAccountForEdit] = useState('');
    const [selectedAccountInDropdown, setSelectedAccountInDropdown] = useState('');
    const [deleteModalState, setDeleteModalState] = useState(false);
    const [incomeSourceEdited, setIncomeSourceEdited] = useState(false);
    const [currentMode, setCurrentMode] = useState(mode);
    const [isDelete, setIsDelete] = useState(false);
    const [beginningYearKey, setBeginningYearKey] = useState<string>('');
    const [endYearKey, setEndYearKey] = useState<string>('');
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const keysToRemove = ['loading', 'error'];
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const [apiErrorToken, setApiErrorToken] = useState(0);

    const isPlanRun = adviceGoals?.global?.isPlanRun;
    const isPlanSavedOnce = adviceGoals?.global?.isPlanSavedOnce;
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const willCallUpdateClientProfile =
        !isPlanSavedOnce || (isFlow2 && !checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']));
    const getSourceKey = (source: any) => source?.accountId;
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const relationships = clientData.relationship;
    const { clientFirstName, clientLastName } = clientData;
    const sourceOwnerOptions = useMemo(() => {
        const options = [
            {
                label: `${clientFirstName} ${clientLastName}`,
                value: 'primary',
                disabled: relationships.length === 0
            }
        ];
        if (relationships.length > 0) {
            relationships.forEach((relationship) => {
                options.push({
                    label: `${relationship.firstName} ${relationship.lastName}`,
                    value: relationship.id,
                    disabled: false
                });
            });
        }

        // Add Joint if FF enabled and both client + co-client exist
        const hasClient = !!clientFirstName && !!clientLastName;
        const hasCoClient = relationships.length > 0;

        if (hasClient && hasCoClient) {
            options.push({
                label: t('TEXT_JOINT'),
                value: 'joint',
                disabled: false
            });
        }

        return options;
    }, [relationships, clientFirstName, clientLastName]);

    const theme = useTheme();

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));

    const getResponsiveWidth = () => {
        if (isXl) return '30%';
        if (isLg || isMd) return '40%';
        return '50%'; // default
    };

    const getCustomDateOptionKey = () => (adviceClient?.relationship?.[0]?.age ? 'option_2' : 'option_1');

    const getSelectedDateOptionKey = (
        savedDate: string | Dayjs | null | undefined,
        savedKey: string | undefined,
        clientDate: string | null,
        coClientDate: string | null
    ) => {
        if (!savedKey || !savedDate) return savedKey || '';
        const normalizedSavedDate = dayjs(savedDate, 'DD-MM-YYYY').format('DD-MM-YYYY');

        if (savedKey === 'option_0') {
            return normalizedSavedDate === clientDate ? savedKey : getCustomDateOptionKey();
        }

        if (savedKey === 'option_1' && coClientDate) {
            return normalizedSavedDate === coClientDate ? savedKey : getCustomDateOptionKey();
        }

        return getCustomDateOptionKey();
    };

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isValid },
        trigger,
        getValues,
        setValue,
        setError,
        clearErrors,
        watch
    } = useForm<AddIncomeSourcesFormInput>({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            sourceOwner: relationships.length === 0 ? sourceOwnerOptions[0].value : '',
            beginningYear: null,
            beginningYearKey: '',
            endYear: null,
            endYearKey: '',
            sourceName: '',
            sourceType: '',
            escalateIncome: 0,
            amount: '',
            frequency: undefined
        }
    });
    const frequency = watch('frequency');
    // useEffect(() => {
    //     if (!checkFFEnabled(FEATURE_NAMES['CLIENTPROFILE_RISKTOLERANCE_REVAMP_AEX-1876']) && frequency === 'Annually') {
    //         const startDate = getValues('beginningYear');
    //         const endDate = getValues('endYear');
    //         if (startDate) {
    //             const year = dayjs(startDate, 'DD-MM-YYYY').get('year');
    //             setValue('beginningYear', dayjs().year(year).month(0).date(1));
    //         }
    //         if (endDate) {
    //             const year = dayjs(endDate, 'DD-MM-YYYY').get('year');
    //             setValue('endYear', dayjs().year(year).month(0).date(1));
    //         }
    //     }
    // }, [frequency]);

    useEffect(() => {
        if (mode === 'edit' && selectedAccountId) {
            setSelectedAccountForEdit(selectedAccountId);
            setSelectedAccountInDropdown(selectedAccountId); // Set dropdown selection when editing directly
            setCurrentMode('edit');
        } else if (mode === 'add') {
            setSelectedAccountForEdit('');
            setSelectedAccountInDropdown(''); // Clear dropdown selection when adding
            setCurrentMode('add');
        }
    }, [mode, selectedAccountId]);
    useEffect(() => {
        if (triggerDeleteAccountId) {
            const currentIncomeSources = clientData?.accounts?.incomeSources || [];
            const updatedSources = currentIncomeSources.filter(
                (source) => getSourceKey(source) !== triggerDeleteAccountId
            );
            setAddedSources(updatedSources);
            setDeletedAccountIds([...deletedAccountIds, triggerDeleteAccountId]);
            setIncomeSourceEdited(true);
            setIsDelete(true);
            const updatedClientData = {
                ...clientData,
                accounts: {
                    ...clientData.accounts,
                    incomeSources: updatedSources
                }
            };
            (async () => {
                await dispatch(saveClient(updatedClientData));
                await dispatch(updateClientIncomeSources(updatedSources));
            })();
            // Re-trigger EWS / MP goal calculator on next plan run, mirroring add/edit flow.
            if (adviceGoals?.formFields?.length > 0) {
                const updatedGoalData = {
                    ...adviceGoals,
                    global: {
                        ...adviceGoals.global,
                        responseGoalCalculator: []
                    }
                };
                dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                dispatch(syncAdviceGoals(updatedGoalData));
            }
            onDeleteTriggered?.();
        }
    }, [triggerDeleteAccountId, clientData?.accounts?.incomeSources, deletedAccountIds, onDeleteTriggered, dispatch]);

    useEffect(() => {
        if (selectedAccountForEdit) {
            const incomeSourceData = addedSources.find((el) => getSourceKey(el) === selectedAccountForEdit);
            reset({
                ...incomeSourceData,
                beginningYear: dayjs(incomeSourceData?.beginningYear, 'DD-MM-YYYY').format('DD-MM-YYYY'),
                endYear: dayjs(incomeSourceData?.endYear, 'DD-MM-YYYY').format('DD-MM-YYYY')
            });
            const clientRetirementDate = addAgeToDate(
                adviceClient?.clientDob,
                adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
            );
            const coClientRetirementDate = addAgeToDate(
                adviceClient?.relationship?.[0]?.age,
                adviceClient?.relationship?.[0]?.retirementAge || DEFAULT_RETIREMENT_AGE
            );
            const clientPlanningDate = addAgeToDate(
                adviceClient?.clientDob,
                adviceClient?.planningAge || DEFAULT_PLANNING_AGE
            );
            const coClientPlanningDate = addAgeToDate(
                adviceClient?.relationship?.[0]?.age,
                adviceClient?.relationship?.[0]?.planningAge || DEFAULT_PLANNING_AGE
            );
            // Set the key states from existing income source data
            setBeginningYearKey(
                getSelectedDateOptionKey(
                    incomeSourceData?.beginningYear,
                    incomeSourceData?.beginningYearKey,
                    clientRetirementDate,
                    coClientRetirementDate
                )
            );
            setEndYearKey(
                getSelectedDateOptionKey(
                    incomeSourceData?.endYear,
                    incomeSourceData?.endYearKey,
                    clientPlanningDate,
                    coClientPlanningDate
                )
            );
        }
    }, [selectedAccountForEdit]);

    const initialFormValues = {
        sourceOwner: relationships.length === 0 ? sourceOwnerOptions[0].value : '',
        beginningYear: null,
        beginningYearKey: '',
        endYear: null,
        endYearKey: '',
        sourceName: '',
        sourceType: '',
        escalateIncome: 0,
        amount: '',
        frequency: undefined
    };
    const handleReset = () => {
        reset(initialFormValues);
        setBeginningYearKey('');
        setEndYearKey('');
    };

    const noUserValuesEntered = () => {
        let data = getValues();
        const userValues = Object.values(data);
        const defaultValues = Object.values(mode === 'add' ? initialFormValues : initialFormValues);
        // check if there is difference between user entered values and default values
        return JSON.stringify(userValues) === JSON.stringify(defaultValues);
    };

    const onSubmit = (data: AddIncomeSourcesFormInput) => {
        if (isValid) {
            let isaccountName = false;
            isaccountName = addedSources
                .filter((source) =>
                    currentMode === 'edit' && selectedAccountForEdit
                        ? getSourceKey(source) !== selectedAccountForEdit
                        : true
                )
                .some((i) => i.sourceName.trim().toLowerCase() === data.sourceName.trim().toLowerCase());
            if (isaccountName) {
                setError('sourceName', { type: 'required', message: t('TEXT_ACCOUNT_EXIST') });
                return;
            } else {
                clearErrors('sourceName');
            }
            let currentAddedSources = [...addedSources];
            if (!selectedAccountForEdit) {
                // Generate accountId on FE for FF-disabled, and for non-flow2 (Advice) when FF is enabled
                if (checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']) || !isFlow2) {
                    data['accountId'] = uuidv4();
                    // Mark as newly created on FE so it is never tagged with an `update` flag in the payload
                    if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) && !isFlow2) {
                        data['isNew'] = true;
                    }
                }
            }
            if (selectedAccountForEdit) {
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    data['update'] = true;
                }
            }
            let amountVal = data['amount'];
            var stringAmountVal = Number(removeNonNumeric(amountVal));
            data['amount'] = stringAmountVal;
            data['beginningYear'] = getYYYYMMDDfromDate(data['beginningYear']);
            data['beginningYearKey'] = beginningYearKey;
            data['endYear'] = getYYYYMMDDfromDate(data['endYear']);
            data['endYearKey'] = endYearKey;
            if (currentMode === 'edit' && selectedAccountForEdit) {
                currentAddedSources = currentAddedSources.map((source) =>
                    getSourceKey(source) === selectedAccountForEdit
                        ? data
                        : willCallUpdateClientProfile &&
                          checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                          source.accountId &&
                          source.update !== true
                        ? { ...source, update: false }
                        : source
                );
            } else {
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    currentAddedSources = currentAddedSources.map((source: any) =>
                        willCallUpdateClientProfile && source.accountId && source.update !== true
                            ? { ...source, update: false }
                            : source
                    );
                }
                currentAddedSources.push(data);
            }
            let incomeSrc = currentAddedSources;
            setAddedSources(currentAddedSources);
            handleReset();
            const copyIncomeSources = Object.assign(clientData.accounts.incomeSources);
            if (
                !selectedAccountForEdit ||
                !noUserValuesEntered() ||
                !(JSON.stringify(copyIncomeSources) === JSON.stringify(incomeSrc))
            )
                setIncomeSourceEdited(true);
            setSelectedAccountForEdit('');
            setSelectedAccountInDropdown(''); // Clear dropdown selection after saving
            setKeyId(uuidv4());
            setCurrentMode('add');
        }
    };

    const onDeleteAccount = async (deletedAccounts: string[] = [], formFields?: any[]) => {
        dispatch(updateGlobalLoaderState(true));
        const originalData = JSON.parse(JSON.stringify(clientData));
        const updatedData = Object.assign({}, originalData);
        keysToRemove.forEach((key) => delete updatedData[key]);
        updatedData['accounts'] = {
            ...updatedData['accounts'],
            incomeSources: addedSources.filter((source: any) => !deletedAccountIds.includes(source.accountId))
        };
        updatedData.stages = [...updatedData.stages, CLIENT_PROFILE_STAGE4];
        if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
            updatedData['accounts']['incomeSources'] = (updatedData['accounts']['incomeSources'] || []).map(
                ({ updatedAt, createdAt, ...rest }: any) =>
                    willCallUpdateClientProfile && rest.accountId && rest.update !== true
                        ? { ...rest, update: false }
                        : { ...rest }
            );
            updatedData['accounts']['investmentsSources'] = (updatedData['accounts']['investmentsSources'] || []).map(
                ({ updatedAt, createdAt, ...rest }: any) =>
                    willCallUpdateClientProfile && rest.accountId && rest.update !== true
                        ? { ...rest, update: false }
                        : { ...rest }
            );
        }

        await dispatch(saveClient(updatedData));
        await dispatch(updateClientIncomeSources(updatedData?.accounts?.incomeSources));

        const formFieldsData = formFields || adviceGoals.formFields;
        if (formFieldsData.length > 0) {
            // dispatch(showLoader());
            const newFormFields = formFieldsData.map((goal: any) => {
                return {
                    ...goal,
                    data: {
                        ...goal.data,
                        sources: {
                            ...goal.data.sources,
                            incomeSource: goal.data.sources.incomeSource.filter(
                                (source: any) => !deletedAccounts.includes(source.accountNumber)
                            )
                        }
                    }
                };
            });

            let updatedgoaldata = {
                ...{ ...adviceGoals, formFields: newFormFields },
                global: {
                    editMode: true,
                    ...adviceGoals?.global,
                    ...(adviceGoals.global?.enablePDFGenerate && {
                        enablePDFGenerate: false,
                        enablePlanDetails: false
                    })
                }
            };

            if (clientData.step === 3) {
                if (callRunpipe) {
                    await callRunpipe(
                        global?.goeConfig?.reallocSchedules ?? [],
                        global?.orgInfo?.orgEmail,
                        adviceClient,
                        adviceGoals,
                        dispatch,
                        updatedgoaldata,
                        updatedData
                    );
                }
            } else {
                updatedgoaldata.global.responseGoalCalculator = [];
                if (isFlow2) updatedgoaldata.global.status = STATUSVARIANTS.INCOMPLETE;
                updatedgoaldata.global.enablePDFGenerate = false;
                dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                await dispatch(syncAdviceGoals(updatedgoaldata));
            }
            handleReset();
            setOpenDrawer(false);
            setAddedSources([]);
        }
        dispatch(updateGlobalLoaderState(false));
    };

    const onComplete = async () => {
        try {
            if (noUserValuesEntered()) {
                // we just deleted an income source
                onDeleteAccount(deletedAccountIds, adviceGoals.formFields);
            } else {
                await trigger();
                let data = getValues();
                if (!selectedAccountForEdit) {
                    // Generate accountId on FE for FF-disabled, and for non-flow2 (Advice) when FF is enabled
                    if (checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']) || !isFlow2) {
                        data['accountId'] = uuidv4();
                        // Mark as newly created on FE so it is never tagged with an `update` flag in the payload
                        if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) && !isFlow2) {
                            data['isNew'] = true;
                        }
                    }
                }
                if (selectedAccountForEdit) {
                    if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                        data['update'] = true;
                    }
                }
                let amountVal = data['amount'];
                var stringAmountVal = Number(removeNonNumeric(amountVal));
                data['amount'] = stringAmountVal;
                data['beginningYear'] = getYYYYMMDDfromDate(data['beginningYear']);
                data['beginningYearKey'] = beginningYearKey;
                data['endYear'] = getYYYYMMDDfromDate(data['endYear']);
                data['endYearKey'] = endYearKey;
                if (isValid) {
                    let isaccountName = false;
                    isaccountName = addedSources
                        .filter((source) =>
                            currentMode === 'edit' && selectedAccountForEdit
                                ? getSourceKey(source) !== selectedAccountForEdit
                                : true
                        )
                        .some((i) => i.sourceName.trim().toLowerCase() === data.sourceName.trim().toLowerCase());
                    if (isaccountName) {
                        setError('sourceName', {
                            type: 'required',
                            message: t('TEXT_ACCOUNT_EXIST')
                        });
                        return;
                    } else {
                        clearErrors('sourceName');
                    }
                    const originalData = JSON.parse(JSON.stringify(clientData));
                    const updatedData = Object.assign({}, originalData);
                    keysToRemove.forEach((key) => delete updatedData[key]);
                    if (currentMode === 'edit' && selectedAccountForEdit) {
                        updatedData['accounts'] = {
                            ...updatedData['accounts'],
                            incomeSources: addedSources?.map((source: any) =>
                                getSourceKey(source) === selectedAccountForEdit
                                    ? data
                                    : willCallUpdateClientProfile &&
                                      checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                                      source.accountId &&
                                      source.update !== true
                                    ? { ...source, update: false }
                                    : source
                            )
                        };
                    } else {
                        updatedData['accounts'] = {
                            ...updatedData['accounts'],
                            incomeSources: [
                                ...(checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
                                    ? addedSources.map((source: any) =>
                                          willCallUpdateClientProfile && source.accountId && source.update !== true
                                              ? { ...source, update: false }
                                              : source
                                      )
                                    : addedSources),
                                data
                            ]
                        };
                    }
                    if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                        updatedData['accounts']['incomeSources'] = (updatedData['accounts']['incomeSources'] || []).map(
                            ({ updatedAt, createdAt, ...rest }: any) => rest
                        );
                        updatedData['accounts']['investmentsSources'] = (
                            updatedData['accounts']['investmentsSources'] || []
                        ).map(({ updatedAt, createdAt, ...rest }: any) =>
                            willCallUpdateClientProfile && rest.accountId && rest.update !== true
                                ? { ...rest, update: false }
                                : { ...rest }
                        );
                    }
                    updatedData.stages = [...updatedData.stages, CLIENT_PROFILE_STAGE4];
                    await dispatch(saveClient(updatedData));
                    // await dispatch(updateClientIncomeSources(updatedData?.accounts?.incomeSources));
                    let originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                    const updatedGoalsData = Object.assign({}, originalGoalsData);
                    // to update the accounts in form fields

                    if (
                        currentMode === 'edit' &&
                        onEditSuccess &&
                        (!noUserValuesEntered() || incomeSourceEdited) &&
                        deletedAccountIds?.length > 0 &&
                        !isDelete
                    )
                        await onEditSuccess();

                    if (updatedGoalsData.global?.hasRunPipe) {
                        updatedGoalsData.global.status = 'incomplete';
                    }

                    if (updatedGoalsData.formFields?.length > 0) {
                        if (updatedGoalsData.global.hasRunPipe) {
                            updatedGoalsData.global.status = 'incomplete';
                        }
                        if (updatedGoalsData.global?.enablePDFGenerate) {
                            updatedGoalsData.global.enablePDFGenerate = false;
                        }

                        if (updatedGoalsData.global?.enablePlanDetails) {
                            updatedGoalsData.global.enablePlanDetails = false;
                        }
                        if (currentMode === 'edit' && (!noUserValuesEntered() || incomeSourceEdited)) {
                            updatedGoalsData.formFields.forEach((goal) => {
                                goal.data.sources.incomeSource.forEach((goalIncomeSrc: any) => {
                                    updatedData.accounts.incomeSources.forEach((incomesrc: any) => {
                                        if (incomesrc.accountId === goalIncomeSrc.accountNumber) {
                                            goalIncomeSrc.accountBalance =
                                                incomesrc.frequency === 'Monthly'
                                                    ? incomesrc.amount * 12
                                                    : incomesrc.amount;
                                            goalIncomeSrc.accountName = incomesrc.sourceName;
                                            goalIncomeSrc.frequency = incomesrc.frequency;
                                            goalIncomeSrc.beginningYear = incomesrc.beginningYear;
                                            goalIncomeSrc.endYear = incomesrc.endYear;
                                        }
                                    });
                                });
                            });
                        }
                        if (clientData.step === 3) {
                            dispatch(updateGlobalLoaderState(true));
                            await callRunpipe?.(
                                global?.goeConfig?.reallocSchedules ?? [],
                                global?.orgInfo?.orgEmail,
                                adviceClient,
                                adviceGoals,
                                dispatch,
                                updatedGoalsData,
                                updatedData
                            );
                            dispatch(updateGlobalLoaderState(false));
                        } else {
                            updatedGoalsData.response = [];
                            updatedGoalsData.global.responseGoalCalculator = [];
                            if (isFlow2) updatedGoalsData.global.status = STATUSVARIANTS.INCOMPLETE;
                            updatedGoalsData.global.enablePDFGenerate = false;
                            dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                            await dispatch(syncAdviceGoals(updatedGoalsData));
                        }
                    }

                    handleReset();
                    setSelectedAccountForEdit('');
                    setOpenDrawer(false);
                    setAddedSources([]);
                }
            }
        } catch (error) {
            console.error('Error in submitting funds', error);
            setApiErrorToken((prev) => prev + 1); // Trigger error message display in UI
        }
    };

    const persistUnsavedIncomeSources = async () => {
        if (incomeSourceEdited) {
            try {
                dispatch(updateGlobalLoaderState(true));
                const originalData = JSON.parse(JSON.stringify(clientData));
                const updatedData = Object.assign({}, originalData);
                keysToRemove.forEach((key) => delete updatedData[key]);
                updatedData['accounts'] = {
                    ...updatedData['accounts'],
                    incomeSources: addedSources.filter((source: any) => !deletedAccountIds.includes(source.accountId))
                };
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    updatedData['accounts']['incomeSources'] = (updatedData['accounts']['incomeSources'] || []).map(
                        ({ updatedAt, createdAt, ...rest }: any) =>
                            willCallUpdateClientProfile && rest.accountId && rest.update !== true
                                ? { ...rest, update: false }
                                : { ...rest }
                    );
                    updatedData['accounts']['investmentsSources'] = (
                        updatedData['accounts']['investmentsSources'] || []
                    ).map(({ updatedAt, createdAt, ...rest }: any) =>
                        willCallUpdateClientProfile && rest.accountId && rest.update !== true
                            ? { ...rest, update: false }
                            : { ...rest }
                    );
                }

                updatedData.stages = [...updatedData.stages, CLIENT_PROFILE_STAGE4];

                await dispatch(saveClient(updatedData));
                await dispatch(updateClientIncomeSources(updatedData?.accounts?.incomeSources));
                let originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                const updatedGoalsData = Object.assign({}, originalGoalsData);
                if (deletedAccountIds.length > 0) {
                    // remove the deleted account from any client goals associated with it
                    updatedGoalsData.formFields.forEach((goal) => {
                        goal.data.sources.incomeSources = goal.data?.sources?.incomeSources?.filter(
                            (source: any) => !deletedAccountIds.includes(source.accountId)
                        );
                    });
                    updatedGoalsData.global.enablePDFGenerate = false;

                    dispatch(syncAdviceGoals(updatedGoalsData));
                }

                if (isPlanRun && incomeSourceEdited) {
                    if (updatedGoalsData.global?.enablePDFGenerate) {
                        updatedGoalsData.global.enablePDFGenerate = false;
                    }
                    if (updatedGoalsData.global?.enablePlanDetails) {
                        updatedGoalsData.global.enablePlanDetails = false;
                    }
                    updatedGoalsData.formFields.forEach((goal) => {
                        goal.data.sources.incomeSource.forEach((goalIncomeSrc: any) => {
                            updatedData.accounts.incomeSources.forEach((incomesrc: any) => {
                                if (incomesrc.accountId === goalIncomeSrc.accountNumber) {
                                    goalIncomeSrc.accountBalance =
                                        incomesrc.frequency === 'Monthly' ? incomesrc.amount * 12 : incomesrc.amount;
                                    goalIncomeSrc.accountName = incomesrc.sourceName;
                                    goalIncomeSrc.frequency = incomesrc.frequency;
                                    goalIncomeSrc.beginningYear = incomesrc.beginningYear;
                                    goalIncomeSrc.endYear = incomesrc.endYear;
                                }
                            });
                        });
                    });
                }
                if (updatedGoalsData.global.hasRunPipe) {
                    updatedGoalsData.global.status = 'incomplete';
                }
                if (clientData.step === 3 && incomeSourceEdited) {
                    await callRunpipe?.(
                        global?.goeConfig?.reallocSchedules ?? [],
                        global?.orgInfo?.orgEmail,
                        adviceClient,
                        adviceGoals,
                        dispatch,
                        updatedGoalsData,
                        updatedData
                    );
                } else {
                    updatedGoalsData.response = [];
                    updatedGoalsData.global.responseGoalCalculator = [];
                    if (isFlow2) updatedGoalsData.global.status = STATUSVARIANTS.INCOMPLETE;
                    updatedGoalsData.global.enablePDFGenerate = false;
                    dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                    await dispatch(syncAdviceGoals(updatedGoalsData));
                }
                handleReset();
                setOpenDrawer(false);
                setAddedSources([]);
                dispatch(updateGlobalLoaderState(false));
            } catch (error) {
                console.error('Error in submitting funds', error);
                dispatch(updateGlobalLoaderState(false));
            }
        }
    };

    const checkUnsavedAndhandleClose = async (flag: boolean) => {
        await persistUnsavedIncomeSources();
        handleReset();
        setOpenDrawer(flag);
        setAddedSources([]);
        dispatch(clearClientError());
    };

    const handleEditClick = (accountId: string) => {
        setSelectedAccountForEdit(accountId);
        setCurrentMode('edit');
    };

    const handleDeleteClick = (accountId: string) => {
        setSelectedAccountForEdit(accountId);
        setDeleteModalState(true);
    };

    const handleConfirmDelete = async () => {
        handleReset();
        const updatedSources = addedSources.filter((source) => getSourceKey(source) !== selectedAccountForEdit);
        const newDeletedAccountIds = [...deletedAccountIds, selectedAccountForEdit];
        setDeletedAccountIds(newDeletedAccountIds);
        setAddedSources(updatedSources);
        setDeleteModalState(!deleteModalState);
        setSelectedAccountForEdit('');
        // Reset dropdown selection and switch to add mode after deletion
        setSelectedAccountInDropdown('');
        setCurrentMode('add');
        setIncomeSourceEdited(true);
        setIsDelete(true);
    };

    const handleCancelDelete = async () => {
        setDeleteModalState(!deleteModalState);
    };

    const isDeleteClicked = () => {
        if (isDelete) {
            return !isDelete;
        } else {
            return !isValid;
        }
    };
    const formValues = watch();
    // Function to check if form is edited
    const checkIfFormIsEdited = () => {
        const incomeSourceData = addedSources.find((el) => getSourceKey(el) === selectedAccountForEdit);
        let data = getValues();
        let amountVal = data['amount'];
        var stringAmountVal = Number(removeNonNumeric(amountVal));
        data['amount'] = stringAmountVal;
        if (data?.beginningYear && data?.endYear) {
            data['beginningYear'] = getYYYYMMDDfromDate(data['beginningYear']);
            data['endYear'] = getYYYYMMDDfromDate(data['endYear']);
        }
        return incomeSourceData
            ? !(JSON.stringify(removeNullUndefined(incomeSourceData)) === JSON.stringify(removeNullUndefined(data)))
            : !isEqual(initialFormValues, formValues);
    };

    return (
        <DrawerComp
            header={t('LABEL_INCOME')}
            open={openDrawer}
            handleClose={checkUnsavedAndhandleClose}
            onSubmit={onComplete}
            drawerIcon={<img src={incomeSource} color={theme.palette.primary[600]} />}
            secondaryButtonLabel={t(currentMode === 'edit' ? 'SAVE_SOURCE' : 'ADD_ANOTHER_INCOME_SOURCE')}
            disableSubmit={isDeleteClicked() || !!infoMessage}
            onSecondarySubmit={handleSubmit(onSubmit)}
            hasChange={checkIfFormIsEdited()}
            isValid={!isValid || !!infoMessage}
            heapId="complete-income-sources-drawer"
            APIError={apiErrorToken}
        >
            {addedSources?.length >= 0 && (
                <>
                    {addedSources?.length > 0 ? (
                        <FundingDrawersDropdown
                            sources={addedSources.map((source) => ({
                                accountId: source.accountId,
                                accountName: source.sourceName,
                                amount:
                                    source.frequency === 'Annually' ? Number(source.amount) : Number(source.amount) * 12
                            }))}
                            selectedSourceId={selectedAccountInDropdown}
                            onSourceSelect={(selectedAccountId: string) => {
                                setSelectedAccountInDropdown(selectedAccountId);
                                setNeedPrepopulation(false);
                            }}
                            onSourceEdit={(selectedAccountId: string) => {
                                if (selectedAccountId) {
                                    handleEditClick(selectedAccountId);
                                }
                            }}
                            onResetForm={handleReset}
                            onModeChange={(mode: 'add' | 'edit') => {
                                setCurrentMode(mode);
                                if (mode === 'add') {
                                    setSelectedAccountForEdit('');
                                }
                            }}
                            title={t('SAVED_INCOME_SOURCES')}
                            placeholderText={t('SELECT_INCOME')}
                            dropdownTestId="saved-income-sources-dropdown"
                        />
                    ) : null}
                </>
            )}
            <ConfirmationDialog
                onCancellation={handleCancelDelete}
                onCloseIconClick={handleCancelDelete}
                onConfirmation={() => handleConfirmDelete()}
                open={deleteModalState}
                content={`${t('DELETE_CONFIRMATION_MODAL_CONTENT')}`}
                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                title={t('DELETE_CONFIRMATION_MODAL_HEADER_TEXT')}
                width={getResponsiveWidth()}
                cardMaxWidth="350px"
                center={true}
            />

            <Grid container marginTop={2} marginBottom={2}>
                <Typography
                    variant="headers.h4"
                    label={t(
                        currentMode === 'edit' && selectedAccountForEdit
                            ? 'DRAWER_EDIT_INCOME_SOURCE'
                            : 'DRAWER_ADD_NEW_INCOME_SOURCE'
                    )}
                />
            </Grid>

            <AddIncomeSourcesForm
                onSubmit={onSubmit}
                handleSubmit={handleSubmit}
                errors={errors}
                control={control}
                sourceOwnerOptions={sourceOwnerOptions}
                keyId={keyId}
                mode={currentMode}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                beginningYearKey={beginningYearKey}
                endYearKey={endYearKey}
                onBeginningYearKeyChange={setBeginningYearKey}
                onEndYearKeyChange={setEndYearKey}
                needPrepopulation={needPrepopulation}
                needPrepopulationOnEdit={needPrepopulationOnEdit}
                setNeedPrepopulation={setNeedPrepopulation}
                setInfoMessage={setInfoMessage}
            />

            {/* Delete Income Source button when in edit mode with feature flag enabled */}
            {(selectedAccountInDropdown || selectedAccountId) && currentMode === 'edit' && (
                <DeleteContainer>
                    <BasicButton
                        variant="text"
                        textVariantStyle="capitalize"
                        color="error"
                        size="medium"
                        onClick={() => handleDeleteClick(selectedAccountInDropdown || selectedAccountId)}
                        data-testid="delete-income-source-button"
                    >
                        {t('DELETE_INCOME')}
                    </BasicButton>
                </DeleteContainer>
            )}
        </DrawerComp>
    );
};

export default AddIncomeSourcesDrawer;
